import { useNavigation } from 'expo-router';
import * as React from 'react';
import { useEffect } from 'react';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import {
    type NavigationState,
    type Route,
    type SceneRendererProps,
    TabBar,
    TabBarItem,
    TabView,
} from 'react-native-tab-view';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H3Regular,
    InfoButton,
    isNonMobileWeb,
    isWeb,
    PillButton,
    ScrollView,
    Spinner,
    useThemeColors,
    useWindowDimensions,
    View,
    XStack,
    YStack,
} from 'smoothie';

import TabHeader from '../../../common/components/TabHeader';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SortDirection } from '../../../common/types/Common';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import UpcomingSessionCard from '../../../guidanceSessions/components/UpcomingSessionCard';
import useSearchGuidanceSessions from '../../../guidanceSessions/hooks/useSearchGuidanceSessions';
import {
    type GuidanceSession,
    GuidanceSessionSortField,
    ScheduledEventStartState,
    UpcomingSessionCardContext,
} from '../../../guidanceSessions/types/GuidanceSessions';
import type { SearchResult } from '../../../http/Fetcher';
import useGetGoals from '../../hooks/useGetGoals';
import { useShowGoalsExplanation } from '../../hooks/useShowGoalsExplanation';
import {
    GoalStatus,
    GoalTabType,
    type IGoal,
    type IGoalGroup,
} from '../../types/Goal';
import { GoalsExplanationSimpleModal } from './GoalsExplanationModal';
import GoalsList from './GoalsList';

const filterGoalsByStatus = (goalGroups: IGoalGroup[], status: GoalStatus) => {
    const getGoalsForGroup = (goalGroup: IGoalGroup) => {
        return goalGroup.goals
            .filter((goal) => goal.status === status)
            .map((goal) => {
                return {
                    category: goalGroup.goalCategory,
                    ...goal,
                } satisfies IGoal;
            });
    };

    // Sort goal groups by position, then extract and sort goals by position
    // Group sorting provides stable ordering when goal positions are equal
    const goals = goalGroups
        .sort((a, b) => a.position - b.position)
        .map((goalGroup) => getGoalsForGroup(goalGroup))
        .flat();
    return goals.sort((a, b) => a.position - b.position);
};

interface GoalsProps {
    goalGroups: IGoalGroup[];
    onRefresh: () => void;
    isLoadingGoalGroups: boolean;
}

const Goals = ({ goalGroups, onRefresh, isLoadingGoalGroups }: GoalsProps) => {
    const { getColor } = useThemeColors();
    const layout = useWindowDimensions();

    const [index, setIndex] = React.useState(0);
    const [routes] = React.useState([
        { key: GoalTabType.ACTIVE, title: 'Active' },
        { key: GoalTabType.COMPLETE, title: 'Complete' },
    ]);

    const activeGoals = filterGoalsByStatus(goalGroups, GoalStatus.IN_PROGRESS);
    const completedGoals = filterGoalsByStatus(goalGroups, GoalStatus.COMPLETE);

    const renderTabBar = (
        props: SceneRendererProps & { navigationState: NavigationState<Route> },
    ) => {
        const showGoalsExplanationModal = useShowGoalsExplanation();

        return (
            <XStack
                alignItems="center"
                marginBottom={20}
                marginLeft={48} // offsets the width of the InfoButton
                marginRight={8}
                gap={2}
            >
                <TabBar
                    {...props}
                    indicatorStyle={{
                        backgroundColor: getColor('$white'),
                        height: '100%',
                        borderRadius: 59,
                        width: isNonMobileWeb ? '49.7%' : '49%',
                    }}
                    style={{
                        flex: 1,
                        backgroundColor: getColor('$gray100'),
                        borderRadius: 59,
                        borderWidth: 2,
                        borderColor: getColor('$gray100'),
                    }}
                    renderTabBarItem={(props) => (
                        <TabBarItem
                            {...props}
                            label={({
                                route,
                                focused,
                            }: {
                                route: Route;
                                focused: boolean;
                            }) => (
                                <BodyMediumRegular
                                    color={
                                        focused
                                            ? getColor('$black')
                                            : getColor('$gray1100')
                                    }
                                >
                                    {route.title}
                                </BodyMediumRegular>
                            )}
                        />
                    )}
                />
                <InfoButton
                    onPress={() => {
                        showGoalsExplanationModal();
                    }}
                />
            </XStack>
        );
    };

    const renderTab = ({ route }: SceneRendererProps & { route: Route }) => {
        switch (route.key as GoalTabType) {
            case GoalTabType.ACTIVE:
                return (
                    <View
                        flex={1}
                        width="100%"
                        $sm={{ width: '90%', marginHorizontal: 'auto' }}
                    >
                        <GoalsList
                            tabType={route.key as GoalTabType}
                            goals={activeGoals}
                            onRefresh={onRefresh}
                            isLoadingGoalGroups={isLoadingGoalGroups}
                        />
                    </View>
                );
            case GoalTabType.COMPLETE:
                return (
                    <View
                        flex={1}
                        width="100%"
                        $sm={{ width: '90%', marginHorizontal: 'auto' }}
                    >
                        <GoalsList
                            tabType={route.key as GoalTabType}
                            goals={completedGoals}
                            onRefresh={onRefresh}
                            isLoadingGoalGroups={isLoadingGoalGroups}
                        />
                    </View>
                );
            default:
                return null;
        }
    };

    return (
        <View height="100%">
            <TabView
                navigationState={{ index, routes }}
                renderTabBar={renderTabBar}
                renderScene={renderTab}
                onIndexChange={setIndex}
                initialLayout={{ width: layout.width }}
            />
        </View>
    );
};

interface NoGoalsProps {
    upcomingSessions: SearchResult<GuidanceSession> | undefined;
}

const NoGoals = ({ upcomingSessions }: NoGoalsProps) => {
    const showGoalsExplanationModal = useShowGoalsExplanation();
    const upcomingSession = upcomingSessions?.results[0];

    return (
        <ScrollView showsVerticalScrollIndicator={false}>
            <YStack
                paddingHorizontal={20}
                gap={16}
                marginBottom={20}
                $sm={{
                    flex: 1,
                    margin: 32,
                    gap: 24,
                    alignItems: 'center',
                }}
            >
                <H3Regular textAlign="center">
                    Welcome to Goals &amp; Tasks
                </H3Regular>
                <BodySmallRegular textAlign="center">
                    {`Fruitful is here to help you make real progress.\nYou'll see goals and tasks from your Guide here after your first session.`}
                </BodySmallRegular>
                <PillButton
                    text="What are goals?"
                    onPress={() => {
                        showGoalsExplanationModal();
                    }}
                    pillProps={{
                        alignSelf: 'center',
                    }}
                />
                <View
                    $sm={{
                        maxWidth: '40vw',
                    }}
                >
                    {upcomingSession ? (
                        <UpcomingSessionCard
                            session={upcomingSession}
                            context={UpcomingSessionCardContext.GOALS}
                        />
                    ) : null}
                </View>
            </YStack>
        </ScrollView>
    );
};

const GoalsSummary = ({
    includeTabHeader = false,
}: {
    includeTabHeader?: boolean;
}) => {
    const { trackViewOnce } = useViewEvents();
    const navigation = useNavigation();
    const user = useAuthedUser();
    const membershipId = user.membership?.id ?? '';
    const {
        data: goalGroups,
        isLoading: isLoadingGoalGroups,
        refetch: refetchGoals,
    } = useGetGoals(user != null, true);

    const { data: upcomingSessions, isLoading: isLoadingUpcomingSessions } =
        useSearchGuidanceSessions(
            {
                userId: user.id,
                membershipId,
                state: ScheduledEventStartState.UPCOMING,
                sortField: GuidanceSessionSortField.START_TIME,
                sortDirection: SortDirection.ASC,
            },
            { enabled: user.membership?.id != null && user.id != null },
        );

    const onRefresh = () => {
        void refetchGoals();
    };

    useEffect(() => {
        navigation.addListener('focus', () => {
            onRefresh();
        });

        return () => {
            navigation.removeListener('focus', () => {
                onRefresh();
            });
        };
    }, [navigation]);

    trackViewOnce('GoalsSummary');

    const hasValidGoals = (goalGroups ?? []).some((group) => {
        return (
            (group.goals ?? []).filter((goal) =>
                [GoalStatus.IN_PROGRESS, GoalStatus.COMPLETE].includes(
                    goal.status,
                ),
            ).length > 0
        );
    });

    const isLoading =
        (goalGroups == null && isLoadingGoalGroups) ||
        isLoadingUpcomingSessions;

    return (
        <View flex={1}>
            {includeTabHeader && <TabHeader headerTitle="Goals" />}
            <Animated.View
                style={{
                    flex: 1,
                    width: '100%',
                    maxWidth: 700,
                    alignSelf: 'center',
                }}
                entering={FadeIn.duration(500)}
                {...(!isWeb && { layout: LinearTransition.duration(300) })}
            >
                {isLoading ? (
                    <Spinner marginTop={96} />
                ) : hasValidGoals ? (
                    <Goals
                        goalGroups={goalGroups ?? []}
                        onRefresh={onRefresh}
                        isLoadingGoalGroups={isLoadingGoalGroups}
                    />
                ) : (
                    <NoGoals upcomingSessions={upcomingSessions} />
                )}
            </Animated.View>
            {isNonMobileWeb && <GoalsExplanationSimpleModal />}
        </View>
    );
};

export default GoalsSummary;
